import React from 'react';
import { Phone, Clock, FileText, AlertTriangle, CheckCircle, BookOpen } from 'lucide-react';
export function HoursOfServicePage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-2501">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-2502">
        <div className="container mx-auto px-4" data-id="element-2503">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-2504">
            Logbook Requirements
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-2505">
            Hours of Service & Daily Log Regulations
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border-b-2 border-yellow-500" data-id="element-2506">
        <div className="container mx-auto px-4 py-4" data-id="element-2507">
          <div className="max-w-4xl mx-auto" data-id="element-2508">
            <div className="flex items-start" data-id="element-2509">
              <AlertTriangle className="text-yellow-600 mr-3 flex-shrink-0 mt-1" size={24} data-id="element-2510" />
              <p className="text-sm text-gray-800" data-id="element-2511">
                <strong data-id="element-2512">Important:</strong> Due to frequent changes in federal
                and state regulations, the Michigan Center for Truck Safety
                cannot ensure the accuracy of the material contained beyond the
                date of publication. For current information, contact the Center
                at{' '}
                <a href="tel:8006824682" className="text-blue-600 hover:underline font-semibold" data-id="element-2513">
                  (800) 682-4682
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-2514">
        <div className="max-w-4xl mx-auto" data-id="element-2515">
          {/* General Information */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2516">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2517">
              <BookOpen className="mr-3 text-[#B1050B]" size={28} data-id="element-2518" />
              General Information
            </h2>

            <div className="space-y-4" data-id="element-2519">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-2520">
                <p className="text-gray-800" data-id="element-2521">
                  The daily log is an{' '}
                  <strong data-id="element-2522">accurate and complete record</strong> of a driver's
                  activities for each <strong data-id="element-2523">24-hour period</strong>.
                </p>
              </div>

              <div className="space-y-3" data-id="element-2524">
                <div className="flex items-start" data-id="element-2525">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2526" />
                  <p className="text-gray-700" data-id="element-2527">
                    Unless exempt (see Short-Haul Provision or 100 air-mile
                    exemption below), all drivers must record their duty status
                    using the daily logbook
                  </p>
                </div>

                <div className="flex items-start" data-id="element-2528">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2529" />
                  <p className="text-gray-700" data-id="element-2530">
                    All entries in the logbook shall be{' '}
                    <strong data-id="element-2531">
                      accurate, legible and in the driver's own handwriting
                    </strong>
                  </p>
                </div>

                <div className="flex items-start" data-id="element-2532">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2533" />
                  <p className="text-gray-700" data-id="element-2534">
                    Employers must retain the daily logs at their principal
                    place of business (previous <strong data-id="element-2535">six months</strong>)
                  </p>
                </div>

                <div className="flex items-start" data-id="element-2536">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2537" />
                  <p className="text-gray-700" data-id="element-2538">
                    Employers must maintain supporting documents to verify the
                    logs are true and accurate
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Short-Haul Provision */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2539">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2540">
              <Clock className="mr-3 text-green-600" size={28} data-id="element-2541" />
              Short-Haul Provision (Exemption)
            </h2>

            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 mb-6" data-id="element-2542">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2543">Applies to:</h3>
              <p className="text-gray-700" data-id="element-2544">
                Drivers who operate CMVs which{' '}
                <strong data-id="element-2545">do not require a CDL</strong> and who operate within a{' '}
                <strong data-id="element-2546">150 air-mile radius</strong> of their normal work
                reporting location
              </p>
            </div>

            <div className="space-y-4" data-id="element-2547">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2548">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2549">Requirements:</h3>
                <ul className="space-y-3 text-gray-700" data-id="element-2550">
                  <li className="flex items-start" data-id="element-2551">
                    <span className="mr-2" data-id="element-2552">•</span>
                    <span data-id="element-2553">
                      May drive a maximum of <strong data-id="element-2554">11 hours</strong> after
                      coming on duty, following{' '}
                      <strong data-id="element-2555">10 or more consecutive hours off duty</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2556">
                    <span className="mr-2" data-id="element-2557">•</span>
                    <span data-id="element-2558">
                      <strong data-id="element-2559">Are not required to keep the daily log</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2560">
                    <span className="mr-2" data-id="element-2561">•</span>
                    <span data-id="element-2562">
                      May not drive beyond the <strong data-id="element-2563">14th hour</strong> after
                      coming on duty five days a week OR after the{' '}
                      <strong data-id="element-2564">16th hour</strong> after coming on duty two days a
                      week
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 100 Air-Mile Exemption */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2565">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2566">
              <Clock className="mr-3 text-blue-600" size={28} data-id="element-2567" />
              100 Air-Mile Exemption
            </h2>

            <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-6 mb-6" data-id="element-2568">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2569">Applies to:</h3>
              <p className="text-gray-700" data-id="element-2570">
                <strong data-id="element-2571">CDL drivers</strong> who operate within a{' '}
                <strong data-id="element-2572">100 air-mile radius</strong> of their normal work
                reporting location
              </p>
            </div>

            <div className="space-y-4" data-id="element-2573">
              <div className="bg-green-50 rounded-lg p-6" data-id="element-2574">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2575">Requirements:</h3>
                <ul className="space-y-3 text-gray-700" data-id="element-2576">
                  <li className="flex items-start" data-id="element-2577">
                    <span className="mr-2" data-id="element-2578">•</span>
                    <span data-id="element-2579">
                      May drive a maximum of <strong data-id="element-2580">11 hours</strong> after
                      coming on duty, following{' '}
                      <strong data-id="element-2581">10 or more consecutive hours off duty</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2582">
                    <span className="mr-2" data-id="element-2583">•</span>
                    <span data-id="element-2584">
                      <strong data-id="element-2585">Are not required to keep the daily log</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2586">
                    <span className="mr-2" data-id="element-2587">•</span>
                    <span data-id="element-2588">
                      May not drive beyond the <strong data-id="element-2589">12th hour</strong> after
                      coming on duty five days a week OR after the{' '}
                      <strong data-id="element-2590">16th hour</strong> after coming on duty one day a
                      week
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2591">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2592">
              Exemption Comparison
            </h2>

            <div className="overflow-x-auto" data-id="element-2593">
              <table className="w-full border-collapse" data-id="element-2594">
                <thead data-id="element-2595">
                  <tr className="bg-gray-200" data-id="element-2596">
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold" data-id="element-2597">
                      Criteria
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold" data-id="element-2598">
                      Short-Haul Provision
                    </th>
                    <th className="border border-gray-300 px-4 py-3 text-left font-bold" data-id="element-2599">
                      100 Air-Mile Exemption
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-2600">
                  <tr className="bg-white" data-id="element-2601">
                    <td className="border border-gray-300 px-4 py-3 font-semibold" data-id="element-2602">
                      Driver Type
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2603">
                      CMVs that do NOT require CDL
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2604">
                      CDL drivers
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-2605">
                    <td className="border border-gray-300 px-4 py-3 font-semibold" data-id="element-2606">
                      Radius
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2607">
                      150 air-miles
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2608">
                      100 air-miles
                    </td>
                  </tr>
                  <tr className="bg-white" data-id="element-2609">
                    <td className="border border-gray-300 px-4 py-3 font-semibold" data-id="element-2610">
                      Max Driving Hours
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2611">
                      11 hours (after 10 consecutive hours off)
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2612">
                      11 hours (after 10 consecutive hours off)
                    </td>
                  </tr>
                  <tr className="bg-gray-50" data-id="element-2613">
                    <td className="border border-gray-300 px-4 py-3 font-semibold" data-id="element-2614">
                      Daily Log Required
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2615">No</td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2616">No</td>
                  </tr>
                  <tr className="bg-white" data-id="element-2617">
                    <td className="border border-gray-300 px-4 py-3 font-semibold" data-id="element-2618">
                      Max On-Duty Time
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2619">
                      14th hour (5 days/week) OR 16th hour (2 days/week)
                    </td>
                    <td className="border border-gray-300 px-4 py-3" data-id="element-2620">
                      12th hour (5 days/week) OR 16th hour (1 day/week)
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Additional Requirements for Exemptions */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2621">
            <h2 className="text-3xl font-bold mb-6" data-id="element-2622">
              Additional Requirements for Both Exemptions
            </h2>

            <div className="space-y-4" data-id="element-2623">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-2624">
                <p className="text-lg mb-2" data-id="element-2625">
                  Drivers using the Short-Haul Provision or the 100 air-mile
                  exemption must:
                </p>
                <ul className="space-y-2 text-gray-100" data-id="element-2626">
                  <li className="flex items-start" data-id="element-2627">
                    <CheckCircle className="mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2628" />
                    <span data-id="element-2629">
                      Comply with the <strong data-id="element-2630">60-/70-hour rule</strong>
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-2631">
                    <CheckCircle className="mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2632" />
                    <span data-id="element-2633">
                      May use the <strong data-id="element-2634">34-hour restart</strong>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Employer Record Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2635">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2636">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-2637" />
              Employer Record Requirements
            </h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-6" data-id="element-2638">
              <p className="text-gray-800 mb-4" data-id="element-2639">
                Employers must maintain{' '}
                <strong data-id="element-2640">accurate and true records</strong> (timecards,
                timesheets, etc.) for each driver using the Short-Haul Provision
                or the 100 air-mile exemption.
              </p>
              <p className="text-gray-800" data-id="element-2641">
                The employer must always have the{' '}
                <strong data-id="element-2642">previous six month's records</strong> on file and
                available for review.
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-6" data-id="element-2643">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-2644">
                These records must identify:
              </h3>

              <div className="space-y-3" data-id="element-2645">
                <div className="bg-white rounded-lg p-4" data-id="element-2646">
                  <p className="font-semibold text-gray-900 mb-1" data-id="element-2647">The driver</p>
                  <p className="text-sm text-gray-600" data-id="element-2648">
                    Full identification of the driver
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4" data-id="element-2649">
                  <p className="font-semibold text-gray-900 mb-1" data-id="element-2650">
                    The time the driver reports for duty
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2651">
                    Exact time driver begins work
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4" data-id="element-2652">
                  <p className="font-semibold text-gray-900 mb-1" data-id="element-2653">
                    The time the driver is released from duty
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2654">
                    Exact time driver ends work
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4" data-id="element-2655">
                  <p className="font-semibold text-gray-900 mb-1" data-id="element-2656">
                    The total on-duty hours each day
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-2657">
                    Complete accounting of daily on-duty time
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2658">
            <h2 className="text-3xl font-bold mb-6" data-id="element-2659">Key Takeaways</h2>

            <div className="space-y-3" data-id="element-2660">
              <div className="flex items-start" data-id="element-2661">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2662" />
                <span data-id="element-2663">
                  Daily logs required for all drivers unless exempt under
                  Short-Haul or 100 air-mile provisions
                </span>
              </div>
              <div className="flex items-start" data-id="element-2664">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2665" />
                <span data-id="element-2666">
                  Logbook entries must be accurate, legible, and in driver's own
                  handwriting
                </span>
              </div>
              <div className="flex items-start" data-id="element-2667">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2668" />
                <span data-id="element-2669">
                  Employers must retain logs for 6 months at principal place of
                  business
                </span>
              </div>
              <div className="flex items-start" data-id="element-2670">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2671" />
                <span data-id="element-2672">
                  Short-Haul: Non-CDL drivers, 150 air-miles, 14th/16th hour
                  limit
                </span>
              </div>
              <div className="flex items-start" data-id="element-2673">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2674" />
                <span data-id="element-2675">
                  100 Air-Mile: CDL drivers, 100 air-miles, 12th/16th hour limit
                </span>
              </div>
              <div className="flex items-start" data-id="element-2676">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2677" />
                <span data-id="element-2678">
                  Both exemptions: 11-hour max driving, 10 consecutive hours off
                  required
                </span>
              </div>
              <div className="flex items-start" data-id="element-2679">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-2680" />
                <span data-id="element-2681">
                  Employers must maintain timecards/timesheets for exempt
                  drivers (6 months)
                </span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-2682">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2683">
              Need Help with Hours of Service Compliance?
            </h2>
            <p className="text-xl mb-6" data-id="element-2684">
              Expert guidance on logbook requirements and hours of service
              regulations
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2685">
              <a href="tel:2484788382" className="inline-flex items-center justify-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2686">
                <Phone className="mr-3" size={24} data-id="element-2687" />
                Call (248) 478-8382
              </a>
              <a href="tel:8006824682" className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2688">
                <Phone className="mr-3" size={24} data-id="element-2689" />
                MI Truck Safety: (800) 682-4682
              </a>
            </div>
            <p className="mt-4 text-gray-300" data-id="element-2690">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}